import React, { useState, useContext } from 'react';
import { Form, Button, Card, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const CreateEmployee = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    hourlyWage: ''
  });
  
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const { 
    firstName, 
    lastName, 
    phone, 
    email, 
    address, 
    city, 
    state, 
    zipCode, 
    hourlyWage 
  } = formData;
  
  const onChange = e => {
    let value = e.target.value;
    
    // For hourly wage, just allow numbers and one decimal point
    if (e.target.name === 'hourlyWage') {
      // Allow digits and decimal point only
      value = value.replace(/[^\d.]/g, '');
      
      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Limit decimal places to 2
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }
    
    setFormData({ ...formData, [e.target.name]: value });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!firstName || !lastName || !hourlyWage) {
      setError('First name, last name, and hourly wage are required');
      return;
    }
    
    // Validate hourly wage as numeric (remove commas first)
    const cleanHourlyWage = hourlyWage.replace(/,/g, '');
    if (isNaN(parseFloat(cleanHourlyWage))) {
      setError('Hourly wage must be a valid number');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch('/api/employees', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          ...formData,
          hourlyWage: formData.hourlyWage.replace(/,/g, '')
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.msg || 'Failed to create employee');
      }
      
      toast.success('Employee created successfully!');
      navigate('/employees');
    } catch (err) {
      console.error('Error creating employee:', err);
      setError(err.message || 'Failed to create employee');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Add New Employee</h2>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            <h4>Personal Information</h4>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={firstName}
                    onChange={onChange}
                    required
                    placeholder="Enter first name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={lastName}
                    onChange={onChange}
                    required
                    placeholder="Enter last name"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={phone}
                    onChange={onChange}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={email}
                    onChange={onChange}
                    placeholder="Enter email address"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <h4 className="mt-4">Address</h4>
            <Form.Group className="mb-3">
              <Form.Label>Street Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={address}
                onChange={onChange}
                placeholder="Enter street address"
              />
            </Form.Group>
            
            <Row>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={city}
                    onChange={onChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={state}
                    onChange={onChange}
                    placeholder="Enter state"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    value={zipCode}
                    onChange={onChange}
                    placeholder="Enter zip code"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <h4 className="mt-4">Employment Information</h4>
            <Form.Group className="mb-3">
              <Form.Label>Hourly Wage ($)</Form.Label>
              <Form.Control
                type="text"
                name="hourlyWage"
                value={hourlyWage}
                onChange={onChange}
                required
                placeholder="Enter hourly wage (e.g., 25.50)"
              />
              <Form.Text className="text-muted">
                Enter the amount as a number (e.g., 25.50)
              </Form.Text>
            </Form.Group>
            
            <div className="d-flex justify-content-between mt-4">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/employees')}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Employee'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreateEmployee;
